import { Dictionary } from 'lodash';
import {
  OffRentRequest,
  Order,
  OrderDocumentType,
  OrderProduct,
  OrderReturnStatus,
  OrderType,
  SplitOrderProductsUpdateRequest,
} from '../../../../../clients/quipli-service/order/order';
import { RentalProductPrice } from '../../../../../clients/quipli-service/pricing/pricing';
import { PartialOffRentQuantities, PartialOffRentUnits } from '../../off-rent-v2/partial-offrent-step';
import {
  CalculateFinancialSummaryRequest,
  DamageWaiverLineItem,
  DeliveryLineItem,
  FeeLineItem,
  FinancialSummary,
  PickupLineItem,
  ProductFeeLineItem,
  ProductLineItem,
  TaxLineItem,
} from '../../../../../clients/quipli-service/financials/financials';
import { orderProductToOrderInputItem } from './off-rent-utils';
import { ProductTrackingType, ProductType } from '../../../../../clients/quipli-service/product/product';
import Dinero from 'dinero.js';
import { toShippingType } from '../../../../../clients/quipli-service/delivery/delivery';
import { safeRound } from '../../../../../util/util';

export function orderToCalculateFinancialSummaryRequest(
  order: Order,
  customPrices: RentalProductPrice[],
  partialOffRentUnitIds: Dictionary<PartialOffRentUnits>,
  isProductPartialOffRentUnits: (opId: number) => boolean,
  partialOffRentQuantities: Dictionary<PartialOffRentQuantities>,
  isProductPartialOffRentQuantity: (opId: number) => boolean,
): CalculateFinancialSummaryRequest {
  const getNewQuantity = (op: OrderProduct) => {
    if (isProductPartialOffRentUnits(op.id)) {
      return op.quantity - (partialOffRentUnitIds[op.id]?.unitIds.length || 0);
    } else if (isProductPartialOffRentQuantity(op.id)) {
      return op.quantity - partialOffRentQuantities[op.id].quantity;
    } else {
      return op.quantity;
    }
  };

  return {
    customerId: order.user.id,
    locationId: order.locationId,
    deliveryType: order.products[0].shippingType,
    customDelivery:
      order.shippingUpdatedAt && order.shippingCharged !== undefined ? order.shippingCharged * 100 : undefined,
    customPickup: order.pickupUpdatedAt && order.pickupCharged !== undefined ? order.pickupCharged * 100 : undefined,
    customTax: order.taxUpdatedAt && order.taxCharged ? order.taxCharged * 100 : undefined,
    customTaxPercentage: order.taxPercentageUpdatedAt && order.taxPercentage,
    customDamageWaiver:
      order.damageWaiverUpdatedAt && order.damageWaiver !== undefined ? order.damageWaiver * 100 : undefined,
    customFees: order.additionalFees
      .filter((af) => af.updatedAt)
      .filter((fee) => fee.updatedAt && fee.updatedAt.toISO() !== null)
      .map((fee) => ({
        feeId: fee.feeId,
        amount: (fee.feeAppliedValue || 0) * 100,
      })),
    customProductFees: order.products.flatMap((product) =>
      product.additionalFees
        .filter((fee) => fee.updatedAt && fee.updatedAt.toISO() !== null)
        .map((fee) => ({
          feeId: fee.attributeId,
          productId: product.product.id,
          productLineItemId: `${product.id}`,
          amount: (fee.feeAppliedValue || 0) * 100,
        })),
    ),
    couponCode: order.discountCoupon,
    additionalDiscount:
      order.additionalDiscount && order.additionalDiscount > 0 ? order.additionalDiscount * 100 : undefined,
    billingAddressId: order.billingAddress?.id,
    deliveryAddressId: order.shippingAddress?.id,
    paymentMethod: order.paymentMethodType,
    isCycleBilled: order.isCycleBilled,
    cycleLength: order.cycleBillingTerms?.cycleLength,
    items: [
      // Will be the items NOT off renting
      ...order.products.map((op) => ({
        ...op,
        quantity: getNewQuantity(op),
      })),
      // Will be new items that we ARE off renting
      ...partialOffRentProductsToOrderProducts(order, partialOffRentUnitIds, isProductPartialOffRentUnits),
      ...partialOffRentProductsToOrderProductsQuantity(
        order,
        partialOffRentQuantities,
        isProductPartialOffRentQuantity,
        customPrices,
      ),
    ].map((op) => {
      return orderProductToOrderInputItem(
        {
          ...op,
          id: `${op.id ?? ''}`,
          rentalEndDate:
            order.isCycleBilled && op.type === OrderType.Rental
              ? op.rentalStartDate.plus(order.cycleBillingTerms!.cycleLength)
              : op.rentalEndDate,
        },
        customPrices.filter(
          (cp) => cp.productId === op.product.id && (cp.orderProductId == op.id || !cp.orderProductId),
        ),
      );
    }),
    taxRateId: order.taxCode,
    documentType: OrderDocumentType.Order,
  };
}

export function partialOffRentProductsToOrderProducts(
  order: Order,
  partialOffRentUnitIds: Dictionary<PartialOffRentUnits>,
  isProductPartialOffRent: (opId: number) => boolean,
): (Omit<OrderProduct, 'id'> & { id: string })[] {
  return Object.keys(partialOffRentUnitIds).flatMap((opId) => {
    if (!isProductPartialOffRent(parseInt(opId))) return [];
    const op = order.products.find((op) => op.id === parseInt(opId))!;
    return [
      {
        ...op,
        id: partialOffRentUnitIds[opId].uniqueId!,
        uniqueId: partialOffRentUnitIds[opId].uniqueId!,
        status: OrderReturnStatus.OnRent,
        quantity: partialOffRentUnitIds[opId].unitIds.length,
        assignedUnits: op.assignedUnits.filter((au) => partialOffRentUnitIds[opId].unitIds.includes(au.id)),
      },
    ];
  });
}

export function partialOffRentProductsToOrderProductsQuantity(
  order: Order,
  partialOffRentQuantities: Dictionary<PartialOffRentQuantities>,
  isProductPartialOffRentQuantity: (opId: number) => boolean,
  customPrices: RentalProductPrice[],
): (Omit<OrderProduct, 'id'> & { id: string })[] {
  return Object.keys(partialOffRentQuantities).flatMap((opId) => {
    if (!isProductPartialOffRentQuantity(parseInt(opId))) return [];
    const op = order.products.find((op) => op.id === parseInt(opId))!;

    let prices: RentalProductPrice[] = [];
    if (op.product.type === ProductType.Custom) {
      const customPricesArr = customPrices.filter(
        (cp) => cp.productId === op.product.id && cp.orderProductId === op.id,
      );
      prices = customPricesArr;
    }

    return [
      {
        ...op,
        id: partialOffRentQuantities[opId].uniqueId!,
        uniqueId: partialOffRentQuantities[opId].uniqueId!,
        status: OrderReturnStatus.OnRent,
        quantity: partialOffRentQuantities[opId].quantity,
        customOrderRentalRates: prices,
      },
    ];
  });
}

export function toPartialOffRentRequest(
  order: Order,
  customPrices: RentalProductPrice[],
  summary: FinancialSummary,
  offRentRequest: OffRentRequest, // will only have order product ids of products with all units being off rented
  currency: Dinero.Currency,
  partialOffRentUnitIds: Dictionary<PartialOffRentUnits>,
  isProductPartialOffRent: (opId: number) => boolean,
  partialOffRentQuantities: Dictionary<PartialOffRentQuantities>,
  isProductPartialOffRentQuantity: (opId: number) => boolean,
): SplitOrderProductsUpdateRequest {
  const cartTotal = summary.lineItems.reduce(
    (acc, line) => (line.type === 'Product' ? acc.add(line.amount) : acc),
    Dinero({ amount: 0, currency }),
  );
  const orderDelivery = summary?.lineItems.find((line) => line.type === 'Delivery') as DeliveryLineItem;
  const damageWaiver = summary?.lineItems.find((line) => line.type === 'DamageWaiver') as DamageWaiverLineItem;
  const delivery = orderDelivery
    ? orderDelivery.amount
    : summary?.lineItems.reduce(
        (acc, line) => (line.type === 'ProductDelivery' ? acc.add(line.amount) : acc),
        Dinero({ amount: 0, currency }),
      );
  const orderPickup = summary?.lineItems.find((line) => line.type === 'Pickup') as PickupLineItem;
  const pickup = orderPickup
    ? orderPickup.amount
    : summary?.lineItems.reduce(
        (acc, line) => (line.type === 'ProductPickup' ? acc.add(line.amount) : acc),
        Dinero({ amount: 0, currency }),
      );
  const tax = summary?.lineItems.find((line) => line.type === 'Tax') as TaxLineItem;
  const couponCode = order.user.tagPricing?.name || order.discountCoupon;
  const couponDiscount =
    summary?.lineItems.find((line) => line.type === 'TagPricing' || line.type === 'Coupon')?.amount ||
    Dinero({ amount: 0, currency });
  const locationFees = (summary?.lineItems.filter((line) => line.type === 'Fee') || []) as FeeLineItem[];
  const existingProductFees = order.products.flatMap((p) => p.additionalFees);
  const discount =
    summary?.lineItems.find((line) => line.type === 'AdditionalDiscount')?.amount ?? Dinero({ amount: 0, currency });

  const newProduct = (
    op: OrderProduct,
  ): Omit<OrderProduct, 'id'> & {
    id: string;
  } => {
    const stringOp = {
      ...op,
      id: op.id.toString(),
    };

    if (op.trackingType === ProductTrackingType.SerialUnits) {
      return {
        ...stringOp,
        assignedUnits:
          op.product.trackingType === ProductTrackingType.SerialUnits
            ? op.assignedUnits.filter(
                (au) =>
                  !isProductPartialOffRent(op.id) ||
                  (isProductPartialOffRent(op.id) && !(partialOffRentUnitIds[op.id]?.unitIds ?? []).includes(au.id)),
              )
            : [],
      };
    } else if (op.trackingType === ProductTrackingType.Quantity) {
      return isProductPartialOffRentQuantity(op.id)
        ? {
            ...stringOp,
            quantity: op.quantity - partialOffRentQuantities[op.id].quantity,
          }
        : stringOp;
    } else {
      return stringOp;
    }
  };

  return {
    locationId: order.locationId,
    billingAddressId: order.billingAddress?.id,
    deliveryAddressId: order.shippingAddress?.id,
    deliveryType: order.products[0].shippingType,
    currency: summary.total.getCurrency(),
    cartTotal: cartTotal.getAmount(),
    damageWaiver: damageWaiver
      ? {
          amount: damageWaiver.amount.getAmount(),
          hasCustomAmount: damageWaiver.hasCustomAmount,
        }
      : undefined,
    delivery: delivery
      ? {
          amount: delivery?.getAmount() || 0,
          hasCustomAmount: orderDelivery?.hasCustomAmount || false,
        }
      : undefined,
    pickup: pickup
      ? {
          amount: pickup?.getAmount() || 0,
          hasCustomAmount: orderPickup?.hasCustomAmount || false,
        }
      : undefined,
    tax: {
      taxCode: tax.taxCodeId,
      amount: tax.amount.getAmount(),
      hasCustomAmount: tax.hasCustomAmount,
      hasCustomPercentage: tax.hasCustomPercentage,
      percentage: tax.percentage,
    },
    coupon: couponCode
      ? {
          code: couponCode,
          amount: couponDiscount.multiply(-1).getAmount(),
          isVip: false,
        }
      : undefined,
    additionalFees: locationFees.map((fee) => {
      const orderFee = order.additionalFees.find((f) => f.feeId === fee.feeId);

      return {
        orderFeeId: orderFee?.id,
        feeId: fee.feeId,
        amount: fee.amount.getAmount(),
        hasCustomAmount: fee.hasCustomAmount,
      };
    }),
    additionalDiscount: !discount.isZero() ? discount.multiply(-1).getAmount() : undefined,
    total: summary.total.getAmount(),
    googleCalculation: summary.googleCalculation,
    securityDeposit:
      order.rentalSecurity !== 0
        ? {
            amount: safeRound(order.rentalSecurity * 100),
            percentage: order.rentalSecurityPercentage,
          }
        : undefined,
    documentType: OrderDocumentType.Order,
    products: (
      [
        ...order.products.map((op) => newProduct(op)),
        ...partialOffRentProductsToOrderProducts(order, partialOffRentUnitIds, isProductPartialOffRent),
        ...partialOffRentProductsToOrderProductsQuantity(
          order,
          partialOffRentQuantities,
          isProductPartialOffRentQuantity,
          customPrices,
        ),
      ] as (Omit<OrderProduct, 'id'> & { id: string })[]
    ).map((op) => {
      const productLineItem = summary.lineItems.find(
        (line) => (line.type === 'Product' || line.type === 'CustomProduct') && line.id === op.id?.toString(),
      )! as ProductLineItem;
      return {
        uniqueId: op.id,
        productId: op.product.id, // productLineItem.productId, TODO: unsure why this was line item?
        type: productLineItem.transactionType,
        amount: productLineItem.unitPrice.getAmount(),
        hasCustomAmount: productLineItem.hasCustomAmount,
        isCustom: op.product.type === (ProductType.Custom as const),
        taxable: !op.isTaxFree,
        name: op.product.type === ProductType.Custom ? op.name : undefined,
        description: op.product.type === ProductType.Custom ? op.description : undefined,
        generalLedgerAccountId: op.generalLedgerAccountId,
        deliveryDriverId: op.deliveryDriverId,
        pickupDriverId: op.pickupDriverId,
        taxCategoryId: op.taxCategoryId,
        equipmentOwner: op.equipmentOwner,
        deliveryType: toShippingType(op.shippingTypeId),
        pickup:
          summary.lineItems
            .find((line) => line.type === 'ProductPickup' && line.productLineItemId === productLineItem.id)
            ?.amount?.getAmount() || 0,
        delivery:
          summary.lineItems
            .find((line) => line.type === 'ProductDelivery' && line.productLineItemId === productLineItem.id)
            ?.amount?.getAmount() || 0,
        securityDeposit: summary.lineItems
          .find((line) => line.type === 'ProductSecurityDeposit' && line.productLineItemId === productLineItem.id)
          ?.amount?.getAmount(),
        quantity: op.trackingType === ProductTrackingType.Quantity ? op.quantity : productLineItem.quantity, // productLineItem.quantity, unsure why this was line item
        additionalFees: summary.lineItems
          .filter((line) => line.type === 'ProductFee' && line.productLineItemId === productLineItem.id)
          .map((line) => {
            const fee = line as ProductFeeLineItem;
            const orderProductFee = existingProductFees.find(
              (f) => f.attributeId === fee.productFeeId && `${f.orderProductId}` === productLineItem.id,
            );

            return {
              orderFeeId: orderProductFee?.id,
              feeId: fee.productFeeId,
              amount: fee.amount.getAmount(),
              hasCustomAmount: fee.hasCustomAmount,
            };
          }),
        discount: op.discountPercentage,
        start: op.rentalStartDate,
        end: op.rentalEndDate,
        customOrderRentalRates:
          op.product.type === ProductType.Custom
            ? customPrices.filter((cp) => cp.productId === op.product.id)
            : customPrices.filter(
                (cp) => cp.productId === op.product.id && (cp.orderProductId === parseInt(op.id) || !cp.orderProductId),
              ),
        taxCode: order.taxCode,
        options: op.options.map((o) => ({
          orderProductId: op.id,
          id: o.productOptionId,
          valueId: o.productOptionValueId,
          name: o.name,
          value: o.value,
        })),
        services: [],
        assignedUnits: op.assignedUnits.map((iu) => iu.id),
        // partial off rent specific stuff
        status: op.status,
        actualRentalStartDate: op.actualRentalStartDate,
        pickupStatus: op.pickupStatus,
        deliveryStatus: op.deliveryStatus,
      };
    }),
  };
}
